import onetime from "@-/com.npmjs.onetime";

import type { SlideComponent } from "#slide-component";
import { getQuizManifest } from "#quizzes-manifest-module";
import { getSlideManifest } from "#slides-manifest-module";

/** Metro `require.context` keys are rooted at `./`. */
const CONTEXT_KEY_PREFIX_PATTERN = /^\.\//;

// `./components` subpath export — the COMPILED MDX CATALOGUE, resolvable ONLY where Metro is the
// bundler (iOS, Android, and Metro-web). It maps each slide slug to the React component
// the offline slide generator compiles into `.gen.js`, via `require.context` (a Metro-only feature).
// NON-Metro consumers (Node/bun seed, Vite, Vitest) use the root export's slug helpers instead. The
// default (non-Metro) resolution of `./components` deliberately throws (see `src/components.ts`) so
// a stray Node import fails loudly rather than silently.
//
// This split is why the slide never renders blank on device again: the root export can no longer
// carry a component variant, so there is no non-Metro `getSlidesComponents()` that could return a
// raw-MDX-string-returning component. React Native's PRODUCTION Fabric renderer strips the "Text
// strings must be rendered within a <Text>" invariant, so a bare-string component renders as a
// zero-layout, unpainted RawText node — blank, no crash, no Sentry error. Requiring Metro for the
// catalogue guarantees the components are always the generator's compiled output.

export type { SlideComponent };

type SlideModule = {
  default: SlideComponent;
};

type SlideRequireContext = {
  keys(): string[];
  (key: string): SlideModule;
};

declare const require: {
  context(directory: string, useSubdirectories: boolean, regExp: RegExp): SlideRequireContext;
};

// Every slide card and quiz is a generated `@grapheme-slides/` module from a level-file or
// lesson-file (`levels/`) `:::slide` or `:::quiz` directive.
// Metro requires literal `require.context` arguments.
const graphemeSlidesContext = require.context(
  "./@grapheme-slides",
  true,
  // oxlint-disable-next-line e18e/prefer-static-regex -- the regex passed to require.context MUST be inline
  /\.gen\.js$/,
);

export const getSlidesComponents: () => Record<string, SlideComponent> = onetime(() => {
  // A slide's slug is authored on its `:::slide` directive, which only the manifest build reads;
  // the manifest records the module generated for it. A disabled slide has no manifest entry (or
  // module), so nothing routes to it.
  const slugBySourcePath = new Map(
    getSlideManifest().map((entry) => [entry.sourcePath, entry.slug] as const),
  );

  return Object.fromEntries(
    graphemeSlidesContext.keys().flatMap((contextKey) => {
      const slug = slugBySourcePath.get(
        `@grapheme-slides/${contextKey.replace(CONTEXT_KEY_PREFIX_PATTERN, "")}`,
      );
      return slug === undefined ? [] : [[slug, graphemeSlidesContext(contextKey).default] as const];
    }),
  );
});

/**
 * Each quiz's compiled MDX (its prompt and `<Quiz>`), keyed by the quiz's UUID key. Quiz modules
 * share `@grapheme-slides/` with slide cards; only the quiz manifest names them, so a quiz never
 * appears among the slides.
 */
export const getQuizComponents: () => Record<string, SlideComponent> = onetime(() => {
  const keyBySourcePath = new Map(
    getQuizManifest().map((entry) => [entry.sourcePath, entry.key] as const),
  );

  return Object.fromEntries(
    graphemeSlidesContext.keys().flatMap((contextKey) => {
      const key = keyBySourcePath.get(
        `@grapheme-slides/${contextKey.replace(CONTEXT_KEY_PREFIX_PATTERN, "")}`,
      );
      return key === undefined ? [] : [[key, graphemeSlidesContext(contextKey).default] as const];
    }),
  );
});
